import * as THREE from 'three';
import { Board } from './Board.js';
import { Controls } from '../ui/Controls.js';
import { ScoreManager } from './ScoreManager.js';
import { Dice } from './Dice.js';
import { ScoreAnimation } from '../ui/ScoreAnimation.js';
import { DiceLayout } from '../ui/DiceLayout.js';
import { gameConfig } from '../config/gameConfig.js';
import { audioManager } from '../audio/AudioManager.js';
import modelLoader from './ModelLoader.js';

export class Game {
    constructor(scene) {
        this.scene = scene;
        this.board = new Board(scene);
        this.scoreManager = new ScoreManager();
        this.scoreAnimation = new ScoreAnimation();
        this.diceLayout = new DiceLayout();
        this.selectedDice = null;
        this.isGameStarted = false;
        this.isCountingDown = false;  // 添加倒计时标志
        this.isStarting = false;  // 添加游戏启动中标志
        // 最大步数
        this.maxSteps = gameConfig.maxSteps;
        this.remainingSteps = this.maxSteps;
        
        // 创建步数显示元素
        this.stepsDisplay = document.createElement('div');
        this.stepsDisplay.style.position = 'fixed';
        this.stepsDisplay.style.top = '20px';
        this.stepsDisplay.style.left = '20px';
        this.stepsDisplay.style.fontSize = '28px';
        this.stepsDisplay.style.color = '#fff';
        this.stepsDisplay.style.textShadow = '2px 2px 4px rgba(0,0,0,0.5)';
        this.stepsDisplay.style.backgroundColor = 'rgba(0, 0, 0, 0.6)';
        this.stepsDisplay.style.padding = '10px 20px';
        this.stepsDisplay.style.borderRadius = '10px';
        this.stepsDisplay.style.fontWeight = 'bold';
        document.body.appendChild(this.stepsDisplay);
        this.updateStepsDisplay();

        // 确保场景已经初始化后再创建控制器
        this.controls = new Controls(scene, this);
        
        // 立即初始化开始按钮和UI元素，不等待模型加载
        this.initStartButton();
        
        // 显示开始按钮容器，让用户可以立即看到UI
        this.showStartUI();
        
        // 在后台初始化游戏资源
        this.initializeGame();
    }


    initStartButton() {
        // 获取已存在的开始按钮容器和按钮
        this.startButtonContainer = document.querySelector('.start-button-container');
        this.startButton = document.getElementById('startButton');
        this.sizeButtons = {};

        // 检查必要的DOM元素是否存在
        if (!this.startButtonContainer || !this.startButton) {
            console.error('无法找到必要的DOM元素');
            return;
        }

        // 获取并设置尺寸按钮 - 直接使用HTML中已设置好的值
        document.querySelectorAll('.size-button').forEach(button => {
            const sizeKey = button.dataset.size;
            const size = gameConfig.boardSizes[sizeKey];
            
            // 为每个尺寸按钮添加点击事件
            button.addEventListener('click', () => {
                Object.values(this.sizeButtons).forEach(btn => {
                    btn.classList.remove('selected');
                });
                button.classList.add('selected');
                
                // 根据选择的尺寸设置棋盘大小
                this.board.size = size;
            });

            this.sizeButtons[sizeKey] = button;
        });

        // 默认选中small尺寸
        if (this.sizeButtons['small']) {
            this.sizeButtons['small'].click();
        }

        // 为开始按钮添加点击事件
        this.startButton.addEventListener('click', () => {
            this.startGame();
        });
    }

    // 显示开始UI界面
    showStartUI() {
        // 移除hidden类，显示开始按钮容器
        const startButtonContainer = document.querySelector('.start-button-container');
        if (startButtonContainer) {
            startButtonContainer.classList.remove('hidden');
        }
        
        // 更新游戏规则中的最大步数
        const maxStepsElement = document.getElementById('maxSteps');
        if (maxStepsElement) {
            maxStepsElement.textContent = gameConfig.maxSteps;
        }
    }
    
    async initializeGame() {
        // 只初始化基本设置，不生成骰子
        this.selectedDice = null;
        this.score = 0;
        this.remainingSteps = this.maxSteps;
        this.updateStepsDisplay();
        
        // 在初始化阶段检查骰子概率配置
        this.checkDiceProbabilities();
        
        // 预加载所有模型（在后台进行，不阻塞UI显示）
        try {
            console.log('开始预加载游戏模型...');
            modelLoader.preloadAllModels().then(() => {
                console.log('游戏模型预加载完成');
            }).catch(error => {
                console.error('预加载模型时出错:', error);
            });
        } catch (error) {
            console.error('启动模型预加载时出错:', error);
        }
    }

    // 检查骰子概率配置
    checkDiceProbabilities() {
        // 验证概率总和是否超过1
        const bombProbability = gameConfig.dice.bomb.enabled ? gameConfig.dice.bomb.probability : 0;
        const teleportProbability = gameConfig.dice.teleport.enabled ? gameConfig.dice.teleport.probability : 0;
        
        if (bombProbability + teleportProbability > 1) {
            // 计算调整后的概率值
            const scale = 1 / (bombProbability + teleportProbability);
            const adjustedBombProb = gameConfig.dice.bomb.enabled ? (gameConfig.dice.bomb.probability * scale).toFixed(2) : 0;
            const adjustedTeleportProb = gameConfig.dice.teleport.enabled ? (gameConfig.dice.teleport.probability * scale).toFixed(2) : 0;
            
            // 显示警告弹窗
            alert(`警告：手榴弹和传送骰子的概率之和超过1，将按比例自动调整概率值\n\n原始概率:\n- 手榴弹: ${bombProbability}\n- 传送骰子: ${teleportProbability}\n\n调整后概率:\n- 手榴弹: ${adjustedBombProb}\n- 传送骰子: ${adjustedTeleportProb}`);
            
            // 更新配置中的概率值
            if (gameConfig.dice.bomb.enabled) {
                gameConfig.dice.bomb.probability *= scale;
            }
            if (gameConfig.dice.teleport.enabled) {
                gameConfig.dice.teleport.probability *= scale;
            }
            
            console.warn('警告：手榴弹和传送骰子的概率之和超过1，已按比例自动调整概率值');
        }
    }

    async startGame() {
        // 如果游戏正在启动中，直接返回
        if (this.isStarting) return;
        
        console.log("Starting game...");
        this.isStarting = true;  // 设置启动中标志
        
        this.isGameStarted = true;
        this.isCountingDown = gameConfig.ui.enableCountdown;  // 根据配置设置倒计时标志
        
        // 清理棋盘，移除所有现有骰子
        this.board.clearBoard();
        
        // 初始化并播放背景音乐
        await audioManager.playBGM();
        
        // 立即隐藏开始按钮容器和游戏规则
        this.startButtonContainer.style.display = 'none';
        const gameRules = document.querySelector('.game-rules');
        if (gameRules) {
            gameRules.style.display = 'none';
        }
        
        // 初始化骰子
        await this.board.initializeDice();
        this.score = 0;
        
        // 打印初始棋盘状态
        this.board.printBoardState('初始棋盘状态（顶部点数）');
        
        // 显示骰子展开图
        this.diceLayout.show();

        if (gameConfig.ui.enableCountdown) {
            // 创建倒计时显示元素
            const countdownDisplay = document.createElement('div');
            countdownDisplay.style.position = 'fixed';
            countdownDisplay.style.top = '50%';
            countdownDisplay.style.left = '50%';
            countdownDisplay.style.transform = 'translate(-50%, -50%)';
            countdownDisplay.style.fontSize = '48px';
            countdownDisplay.style.color = '#fff';
            countdownDisplay.style.textShadow = '2px 2px 4px rgba(0,0,0,0.5)';
            countdownDisplay.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
            countdownDisplay.style.padding = '20px 40px';
            countdownDisplay.style.borderRadius = '15px';
            countdownDisplay.style.fontWeight = 'bold';
            countdownDisplay.style.zIndex = '9999';
            document.body.appendChild(countdownDisplay);

            // 执行倒计时
            let countdown = gameConfig.ui.countdownDuration;
            return new Promise(resolve => {
                const countdownInterval = setInterval(() => {
                    if (countdown > 0) {
                        countdownDisplay.textContent = `游戏开始倒计时: ${countdown}`;
                        countdown--;
                    } else {
                        clearInterval(countdownInterval);
                        document.body.removeChild(countdownDisplay);
                        this.isCountingDown = false;  // 倒计时结束，清除标志
                        this.isStarting = false;  // 清除启动中标志
                        resolve();
                    }
                }, 1000);
            }).then(() => {
                // 倒计时结束后开始检查并合并骰子
                this.checkAndMergeDice();
            });
        } else {
            // 如果不需要倒计时，直接开始游戏
            this.isCountingDown = false;
            this.isStarting = false;  // 清除启动中标志
            this.checkAndMergeDice();
        }
    }

    // 检查并合并骰子
    async checkAndMergeDice() {
        if (!this.isGameStarted) return;
        
        // 检查是否启用了自动合并检查
        if (!gameConfig.dice.enableAutoMergeCheck) return;

        const mergeableDices = this.board.checkMergeable();
        if (mergeableDices) {
            console.log("找到可合并的骰子组:", mergeableDices);
            // 计算分数
            const points = mergeableDices.reduce((sum, dice) => sum + dice.topValue, 0) * 10;
            console.log("基础分数（骰子面值总和 * 10）:", points);
            // 添加额外的合并奖励分数（基于合并的骰子数量）
            const bonusPoints = Math.pow(2, mergeableDices.length - 2) * 10;
            console.log("额外奖励分数:", bonusPoints);
            const totalPoints = points + bonusPoints;
            console.log("总分数:", totalPoints);
            
            // 更新分数并显示动画
            this.scoreManager.addScore(totalPoints);
            
            // 获取第一个骰子的屏幕坐标作为动画起点
            const firstDice = mergeableDices[0];
            const vector = new THREE.Vector3();
            firstDice.mesh.getWorldPosition(vector);
            vector.project(this.scene.camera);
            const x = (vector.x * 0.5 + 0.5) * window.innerWidth;
            const y = (-vector.y * 0.5 + 0.5) * window.innerHeight;
            console.log("分数气泡动画起点坐标:", { x, y });
            
            // 显示分数动画
            console.log("调用分数动画显示方法，参数:", { totalPoints, x, y });
            this.scoreAnimation.showScoreAnimation(totalPoints, x, y);
            
            await this.board.removeDiceGroup(mergeableDices);
            // 应用重力效果
            await this.board.applyGravity();
            // 继续检查是否还有可合并的骰子
            setTimeout(() => this.checkAndMergeDice(), 500);
        }
    }

    gameOver() {
        this.isGameStarted = false;
        this.startButtonContainer.style.display = 'flex';
        this.startButton.textContent = '重新开始';
        
        // 显示最终得分
        alert(`游戏结束！\n最终得分：${this.scoreManager.getScore()}`);
    }

    dispose() {
        if (this.stepsDisplay && this.stepsDisplay.parentNode) {
            this.stepsDisplay.parentNode.removeChild(this.stepsDisplay);
        }
        if (this.startButtonContainer && this.startButtonContainer.parentNode) {
            this.startButtonContainer.parentNode.removeChild(this.startButtonContainer);
        }
    }

    update() {
        if (!this.isGameStarted) {
            return;
        }

        if (this.checkGameOver()) {
            console.log("Game Over!");
            this.gameOver();
        }
    }

    async handleClick(clickedDice) {
        if (!this.isGameStarted || this.remainingSteps <= 0 || this.isCountingDown) return;  // 添加倒计时检查

        // 如果点击的是手榴弹
        if (clickedDice.type === 'grenade') {
            // 检查棋盘状态
            if (!this.board.isBoardFull() || this.board.hasMergePossibility() || this.board.hasEmptySpaces()) {
                console.log('当前棋盘未铺满或存在可合并/生成的骰子，不能进行选择操作');
                return;
            }
            // 获取受影响的骰子
            const affectedDices = this.board.getBombAffectedDices(clickedDice);
            // 将手榴弹本身也加入到要移除的列表中
            const dicesToRemove = [clickedDice, ...affectedDices];
            
            // 计算得分
            // 基础分数：被炸掉的普通骰子点数之和乘以10
            const normalDices = dicesToRemove.filter(dice => dice.type !== 'grenade');
            const basePoints = normalDices.reduce((sum, dice) => sum + dice.topValue, 0) * 10;
            
            // 额外奖励：基于连锁爆炸的数量
            const grenades = dicesToRemove.filter(dice => dice.type === 'grenade');
            const chainBonus = grenades.length > 1 ? Math.pow(2, grenades.length - 1) * 20 : 0;
            
            const totalPoints = basePoints + chainBonus;
            
            // 更新分数并显示动画
            if (totalPoints > 0) {
                this.scoreManager.addScore(totalPoints);
                
                // 获取第一个骰子的屏幕坐标作为动画起点
                const vector = new THREE.Vector3();
                clickedDice.mesh.getWorldPosition(vector);
                vector.project(this.scene.camera);
                const x = (vector.x * 0.5 + 0.5) * window.innerWidth;
                const y = (-vector.y * 0.5 + 0.5) * window.innerHeight;
                
                // 显示分数动画
                this.scoreAnimation.showScoreAnimation(totalPoints, x, y);
            }
            
            // 触发手榴弹的爆炸动画
            await clickedDice.explode();
            
            // 移除手榴弹和受影响的骰子
            await this.board.removeDiceGroup(dicesToRemove);
            
            // 应用重力效果
            await this.board.applyGravity();
            
            // 检查是否有可以合并的骰子
            await this.checkAndMergeDice();
            
            // 减少剩余步数并更新显示
            this.remainingSteps--;
            this.updateStepsDisplay();
            return;
        }

        // 打印被点击骰子的面值信息，按BoxGeometry的面顺序[右、左、上、下、前、后]输出
        console.log('点击的骰子面值:', {
            右面: clickedDice.faces[0],
            左面: clickedDice.faces[1],
            上面: clickedDice.faces[2],
            下面: clickedDice.faces[3],
            前面: clickedDice.faces[4],
            后面: clickedDice.faces[5]
        });

        // 检查棋盘状态
        if (!this.board.isBoardFull() || this.board.hasMergePossibility() || this.board.hasEmptySpaces()) {
            console.log('当前棋盘未铺满或存在可合并/生成的骰子，不能进行选择操作');
            return;
        }

        if (this.selectedDice === clickedDice) {
            // 取消选择
            this.selectedDice.setHighlight(false);
            // 隐藏长按提示
            this.controls.dragHintElement.style.display = 'none';
            this.selectedDice = null;
        } else if (!this.selectedDice) {
            // 第一次选择
            this.selectedDice = clickedDice;
            this.selectedDice.setHighlight(true);
            
            // 如果选中的是传送骰子，显示长按提示
            if (clickedDice.isTeleport) {
                const controls = this.controls;
                const longPressSeconds = controls.longPressDuration / 1000;
                controls.dragHintElement.textContent = `长按${longPressSeconds}秒可拖动骰子`;
                controls.dragHintElement.style.display = 'block';
                
                // 3秒后自动隐藏提示
                setTimeout(() => {
                    if (this.selectedDice === clickedDice) { // 确保还是同一个骰子被选中
                        controls.dragHintElement.style.display = 'none';
                    }
                }, 3000);
            }
        } else {
            
            // 取消上一个选中的骰子高亮
            this.selectedDice.setHighlight(false);
            // 选中新的骰子
            this.selectedDice = clickedDice;
            this.selectedDice.setHighlight(true);
            
            // 如果选中的是传送骰子，显示长按提示
            if (clickedDice.isTeleport) {
                const controls = this.controls;
                const longPressSeconds = controls.longPressDuration / 1000;
                controls.dragHintElement.textContent = `长按${longPressSeconds}秒可拖动骰子`;
                controls.dragHintElement.style.display = 'block';
                
                // 3秒后自动隐藏提示
                setTimeout(() => {
                    if (this.selectedDice === clickedDice) { // 确保还是同一个骰子被选中
                        controls.dragHintElement.style.display = 'none';
                    }
                }, 3000);
            } else {
                // 如果不是传送骰子，确保提示不显示
                this.controls.dragHintElement.style.display = 'none';
            }
        }
    }

    // 处理点击空白处
    handleClickEmpty() {
        if (this.selectedDice) {
            this.selectedDice.setHighlight(false);
            // 隐藏长按提示
            this.controls.dragHintElement.style.display = 'none';
            this.selectedDice = null;
        }
    }

    checkGameOver() {
        // 如果游戏未开始，返回 false
        if (!this.isGameStarted) {
            return false;
        }

        // 检查剩余步数
        if (this.remainingSteps <= 0) {
            return true;
        }

        // 原有的游戏结束检查逻辑...
    }

    // 更新步数显示
    updateStepsDisplay() {
        this.stepsDisplay.textContent = `剩余步数: ${this.remainingSteps}`;
    }
}